import {
  Box,
  Button,
  Divider,
  Link,
  List,
  ListItem,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'

import logoDark from '~/assets/images/corp/logo-dark.svg'
import rem from '~/utils/ui'

type TFooterLink = {
  label: string
  onClick?: () => void
  url?: string
}

type TFooterLinkGroup = {
  title: string
  links: TFooterLink[]
}

const FOOTER_LINK_GROUPS: TFooterLinkGroup[] = [
  {
    title: 'Support',
    links: [
      {
        label: 'Docs',
        url: 'https://docs.replify.ai',
      },
      {
        label: 'Chat with Support',
        onClick: () => {
          const chatbotIcon = document.getElementById('chatbot-icon')

          if (chatbotIcon) {
            chatbotIcon.dispatchEvent(new Event('click', { bubbles: true }))
          }
        },
      },
    ],
  },
  {
    title: 'Company',
    links: [
      {
        label: 'Case Studies',
        url: 'https://replify.ai/case-studies',
      },
      {
        label: 'Schedule a Demo',
        url: 'https://replify.ai/demo',
      },
    ],
  },
  {
    title: 'Legal',
    links: [
      {
        label: 'Terms & Conditions',
        url: 'https://www.replify.ai/terms-of-use',
      },
      {
        label: 'Privacy Policy',
        url: 'https://www.replify.ai/privacy-policy',
      },
    ],
  },
]

function Footer() {
  const theme = useTheme()

  return (
    <Stack
      component="footer"
      sx={{
        paddingBlock: rem(20),
        paddingInline: rem(32),
        backgroundColor: theme.palette.grey[50],
        alignItems: 'center',
        overflow: 'hidden',
        borderTop: 1,
        borderColor: theme.palette.divider,
      }}
      useFlexGap
    >
      <Stack spacing={1} useFlexGap sx={{ maxWidth: rem(1024) }}>
        <Box
          sx={{
            display: 'grid',
            gridAutoFlow: { xxxs: 'row', sm: 'column' },
            gridAutoColumns: '1fr',
            gridAutoRows: 'auto',
            gap: rem(40),
          }}
        >
          <Stack spacing={1} alignItems="flex-start" useFlexGap>
            <Box
              component="img"
              alt="logo"
              src={logoDark}
              sx={{ height: rem(28) }}
            />
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary, fontSize: rem(14) }}
            >
              AI receptionist and sales platform for modern teams.
            </Typography>
          </Stack>

          {FOOTER_LINK_GROUPS.map(({ links, title }, index) => (
            <Stack key={`${title}-${index.toString()}`} spacing={1}>
              <Typography
                variant="h3"
                sx={{ fontSize: rem(14), fontWeight: 700 }}
              >
                {title}
              </Typography>
              <List
                sx={{
                  '&&&': {
                    padding: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: rem(6),
                  },
                }}
              >
                {links.map(({ label, onClick, url }) => (
                  <ListItem key={label} sx={{ padding: 0 }}>
                    {!!url && (
                      <Link
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: rem(14),
                          textDecoration: 'none',
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {label}
                      </Link>
                    )}
                    {!!onClick && (
                      <Button
                        variant="text"
                        onClick={onClick}
                        disableRipple
                        disableTouchRipple
                        disableFocusRipple
                        sx={{
                          '&&&': {
                            padding: 0,
                            borderRadius: 0,
                            minHeight: 'initial',
                            height: 'auto',
                            minWidth: 'initial',
                            width: 'auto',
                            fontSize: rem(14),
                            fontWeight: 400,
                            letterSpacing: 0,
                            color: theme.palette.text.secondary,
                            lineHeight: rem(21),

                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: theme.palette.tertiary1,
                            },
                          },
                        }}
                      >
                        {label}
                      </Button>
                    )}
                  </ListItem>
                ))}
              </List>
            </Stack>
          ))}
        </Box>
        <Divider flexItem />
        <Typography
          variant="body1"
          sx={{
            fontSize: rem(12),
            color: theme.palette.text.secondary,
            marginRight: 'auto',
          }}
        >
          © {new Date().getFullYear()} Replify. All rights reserved.
        </Typography>
      </Stack>
    </Stack>
  )
}

export default Footer
